import React, { useState } from 'react';
import { Save, Plus, Trash2, Info } from 'lucide-react';
import { useContract } from '../../hooks/useContract';
import toast from 'react-hot-toast';

interface WheelSegment {
  id: string;
  label: string;
  value: number;
  color: string;
  probability: number;
}

interface FortuneWheelTabProps {
  inputClasses: string;
  currentWallet: string;
}

const DEFAULT_SEGMENTS: WheelSegment[] = [
  { id: '1', label: '100', value: 100, color: '#4CAF50', probability: 0.3 },
  { id: '2', label: '200', value: 200, color: '#2196F3', probability: 0.25 },
  { id: '3', label: '300', value: 300, color: '#9C27B0', probability: 0.2 },
  { id: '4', label: '500', value: 500, color: '#F44336', probability: 0.15 },
  { id: '5', label: '1000', value: 1000, color: '#FFC107', probability: 0.1 }
];

export function FortuneWheelTab({ inputClasses, currentWallet }: FortuneWheelTabProps) {
  const { isDemoMode } = useContract();
  const [segments, setSegments] = useState<WheelSegment[]>(() => {
    const saved = localStorage.getItem('fortuneWheel_segments_' + currentWallet);
    return saved ? JSON.parse(saved) : DEFAULT_SEGMENTS;
  });
  const [spinCost, setSpinCost] = useState(() => {
    const saved = localStorage.getItem('fortuneWheel_spinCost_' + currentWallet);
    return saved ? parseInt(saved) : 50;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Vérifier que les probabilités totalisent 1
    const totalProbability = segments.reduce((sum, seg) => sum + seg.probability, 0);
    if (Math.abs(totalProbability - 1) > 0.001) {
      toast.error('La somme des probabilités doit être égale à 1');
      return;
    }

    try {
      // Sauvegarder les configurations
      localStorage.setItem('fortuneWheel_segments_' + currentWallet, JSON.stringify(segments));
      localStorage.setItem('fortuneWheel_spinCost_' + currentWallet, spinCost.toString());

      toast.success(isDemoMode ? 'Configuration sauvegardée (Mode Démo)' : 'Configuration sauvegardée');
    } catch (error) {
      console.error('Error saving fortune wheel config:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const addSegment = () => {
    const newId = (segments.length + 1).toString();
    setSegments([...segments, {
      id: newId,
      label: '100',
      value: 100,
      color: '#808080',
      probability: 0.1
    }]);
  };

  const removeSegment = (id: string) => {
    setSegments(segments.filter(s => s.id !== id));
  };

  const updateSegment = (id: string, updates: Partial<WheelSegment>) => {
    setSegments(segments.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {isDemoMode && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center">
            <Info className="text-yellow-400 mr-2" size={20} />
            <p className="text-yellow-700">
              Mode démo actif : Les changements seront sauvegardés localement uniquement
            </p>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Configuration de la Roue
        </h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coût par tour
          </label>
          <input
            type="number"
            value={spinCost}
            onChange={(e) => setSpinCost(parseInt(e.target.value))}
            className={inputClasses}
            min="1"
          />
        </div>

        <div className="space-y-4">
          {segments.map((segment) => (
            <div key={segment.id} className="grid grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Label
                </label>
                <input
                  type="text"
                  value={segment.label}
                  onChange={(e) => updateSegment(segment.id, { label: e.target.value })}
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valeur
                </label>
                <input
                  type="number"
                  value={segment.value}
                  onChange={(e) => updateSegment(segment.id, { value: parseInt(e.target.value) })}
                  className={inputClasses}
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur
                </label>
                <input
                  type="color"
                  value={segment.color}
                  onChange={(e) => updateSegment(segment.id, { color: e.target.value })}
                  className="w-full h-10 rounded-lg cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Probabilité
                </label>
                <input
                  type="number"
                  value={segment.probability}
                  onChange={(e) => updateSegment(segment.id, { probability: parseFloat(e.target.value) })}
                  className={inputClasses}
                  step="0.01"
                  min="0"
                  max="1"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeSegment(segment.id)}
                  className="p-2 text-red-600 hover:text-red-700"
                  disabled={segments.length <= 2}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addSegment}
          className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-700"
        >
          <Plus size={20} />
          Ajouter un segment
        </button>
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          <Save size={20} />
          Sauvegarder la Configuration
        </button>
      </div>
    </form>
  );
}